import { useEffect, useState } from 'react';

// The logged-in user's own profile, live from GET /me — not just the thin
// snapshot cached in the login cookie. /me is also the authoritative source
// for "which role(s) do I have", since roles are inherited via group
// membership and can change without a fresh login.
export const useCurrentUser = () => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchMe = async () => {
      setLoading(true);
      setError(false);
      try {
        const Cookies = (await import('universal-cookie')).default;
        const cookies = new Cookies();
        const userCookie = cookies.get('101Obex');
        if (!userCookie || !userCookie.token) {
          setError(true);
          return;
        }

        const ObexRequestsHandlerP80 = (await import('../handlers/ObexRequestsHandlerP80')).default;
        let res = await ObexRequestsHandlerP80.get('/me', { access_token: userCookie.token });
        if (res.data) res = res.data;

        const meData = res?.response ?? res?.result?.response;
        const profile = meData?.user;
        if (!profile) {
          setError(true);
          return;
        }

        const rolesList = Array.isArray(meData?.roles) ? meData.roles : [];
        const roleNames: string[] = rolesList.map((r: any) => r.name).filter(Boolean);

        // /me doesn't return group membership yet, so cross-reference /list
        // (which does, via group_names) to find this user's own row.
        let groupNames: string[] = [];
        try {
          let listRes = await ObexRequestsHandlerP80.get('/list', {
            access_token: userCookie.token,
            page: 1,
            page_size: 200,
          });
          if (listRes.data) listRes = listRes.data;
          const listData = listRes?.response ?? listRes?.result?.response;
          const rows = Array.isArray(listData?.data) ? listData.data : [];
          const myRow = rows.find((u: any) => String(u.id) === String(profile.id));
          if (myRow && typeof myRow.group_names === 'string' && myRow.group_names.trim() !== '') {
            groupNames = myRow.group_names.split(';').map((s: string) => s.trim()).filter(Boolean);
          }
        } catch (groupsError) {
          console.error('Error fetching current user\'s groups:', groupsError);
        }

        setCurrentUser({
          id: String(profile.id),
          name: `${profile.name || ''} ${profile.last_name || ''}`.trim() || profile.username,
          firstName: profile.name || '',
          lastName: profile.last_name || '',
          email: profile.email,
          username: profile.username,
          status: profile.status,
          must_change_password: profile.must_change_password === true || profile.must_change_password === 'true' || profile.must_change_password === 1 || profile.must_change_password === '1',
          // `position` is left unset — it means "job title" (Cargo/Puesto de
          // Trabajo), which isn't in the DB yet. `role` is the security-role
          // badge shown in the sidebar; both are distinct from each other.
          role: roleNames.join(', ') || undefined,
          inheritedRoles: roleNames,
          groupNames,
          permissions: Array.isArray(meData?.permissions) ? meData.permissions : [],
          // Not returned by /me yet — see gap list.
          lastLogin: new Date().toISOString(),
          // From the profiles table (added via /me's LEFT JOIN). Expected to
          // be a full data URI ("data:image/png;base64,...."), not bare
          // base64 payload — <img src> can render it directly either way.
          photoUrl: profile.avatar_base64 || undefined,
          dni: profile.dni || undefined,
          phone: profile.phone || undefined,
          birthDate: profile.birth_date || undefined,
          address: profile.address || undefined,
          employeeId: profile.employee_code || undefined,
          position: profile.job_title || undefined,
          hireDate: profile.hire_date || undefined,
          hourlyCost: profile.hourly_cost != null ? Number(profile.hourly_cost) : undefined,
          iban: profile.iban || undefined,
          emergencyContactName: profile.emergency_contact_name || undefined,
          emergencyContactPhone: profile.emergency_contact_phone || undefined,
          notes: profile.notes || undefined,
        });
      } catch (fetchError) {
        console.error('Error fetching current user (/me):', fetchError);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMe();
  }, []);

  return { currentUser, loading, error };
};
